if (!customElements.get('marame-product-video')) {
  customElements.define('marame-product-video', class MarameProductVideo extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-video-open]');
      this.player = this.querySelector('[data-video-player]');
      this.error = this.querySelector('[data-video-error]');
      this.fallback = this.querySelector('[data-video-fallback]');
      this.template = this.querySelector('[data-video-template]');
      this.accordion = this.querySelector('[data-video-accordion]');
      if (!this.dialog?.showModal || !this.trigger || !this.template) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-controls', this.dialog.id);
      this.trigger.setAttribute('aria-expanded', 'false');
      this.addEventListener('shopify:block:select', () => {
        if (this.accordion) this.accordion.open = true;
      }, options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-video-open]')) {
          if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.show();
        } else if (event.target.closest('[data-video-close]')) {
          this.hide();
        }
      }, options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.hide();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) { this.stopSheetMotion(); this.release(); }
      }, options);
      this.dialog.addEventListener('pointerdown', event => { this.backdropDown = this.isBackdrop(event); }, options);
      this.dialog.addEventListener('click', event => {
        if (this.backdropDown && this.isBackdrop(event)) this.hide();
        this.backdropDown = false;
      }, options);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.hide(false);
    }

    show() {
      if ((this.dialog.open && !this.closing) || this.hidden) return;
      const start = this.closing ? this.sheetPosition(this.sheetSpring().axis) : null;
      this.stopSheetMotion();
      this.release();
      this.error.hidden = true;
      if (this.fallback) this.fallback.hidden = true;
      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('marame-video-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.video = this.template.content.querySelector('video').cloneNode(true);
      const video = this.video;
      // Only this explicit open mounts the sources/poster. Playback remains native and user initiated.
      const showError = () => {
        if (this.video === video && this.dialog.open) {
          this.error.hidden = false;
          if (this.fallback) this.fallback.hidden = false;
        }
      };
      video.addEventListener('error', showError, { once: true });
      video.querySelectorAll('source').forEach(source => source.addEventListener('error', showError, { once: true }));
      this.player.replaceChildren(video);
      const playback = video.play();
      playback?.catch(() => {});
      this.querySelector('[data-video-close]').focus({ preventScroll: true });
      this.animateSheet(false, start);
    }

    release() {
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        this.video.removeAttribute('poster');
        this.video.load();
        this.video.remove();
        this.video = null;
      }
      this.player?.replaceChildren();
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (!document.querySelector('marame-product-video dialog[open]')) document.body.classList.remove('marame-video-open');
    }

    sheetSpring() {
      return { axis: 'Y', travel: window.matchMedia?.('(min-width: 1024px)')?.matches ? 3 : 100, damping: 34, stiffness: 300, mass: 0.9 };
    }

    sheetPosition(axis) {
      if (!window.DOMMatrix || !window.getComputedStyle) return null;
      const transform = window.getComputedStyle(this.dialog).transform;
      if (!transform || transform === 'none') return 0;
      const matrix = new window.DOMMatrix(transform);
      const extent = this.dialog.getBoundingClientRect()[axis === 'X' ? 'width' : 'height'];
      return extent ? 100 * matrix[axis === 'X' ? 'm41' : 'm42'] / extent : null;
    }

    stopSheetMotion() {
      this.motionToken = (this.motionToken || 0) + 1;
      if (this.closeTimer) clearTimeout(this.closeTimer);
      this.closeTimer = null;
      this.sheetAnimation?.cancel();
      this.sheetAnimation = null;
      this.closing = false;
      this.dialog?.removeAttribute('data-closing');
    }

    animateSheet(exiting, start) {
      if (!this.dialog.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return null;
      const { axis, travel, damping, stiffness, mass } = this.sheetSpring();
      const alpha = damping / (2 * mass);
      const discriminant = alpha * alpha - stiffness / mass;
      const remaining = (time) => {
        if (Math.abs(discriminant) < 0.00001) return Math.exp(-alpha * time) * (1 + alpha * time);
        const omega = Math.sqrt(Math.abs(discriminant));
        if (discriminant < 0) return Math.exp(-alpha * time) * (Math.cos(omega * time) + alpha / omega * Math.sin(omega * time));
        const slow = -alpha + omega, fast = -alpha - omega;
        return (-fast * Math.exp(slow * time) + slow * Math.exp(fast * time)) / (slow - fast);
      };
      const from = start ?? (exiting ? 0 : travel), to = exiting ? travel : 0;
      const frames = [];
      let duration = 0;
      for (let step = 0; step <= 100; step++) {
        const time = step / 100;
        const rest = remaining(time);
        frames.push({ transform: `translate${axis}(${to + (from - to) * rest}%)` });
        duration = step * 10;
        if (time > 0.25 && Math.abs(rest) < 0.001 && Math.abs(remaining(time + 0.01) - rest) < 0.0001) break;
      }
      frames[frames.length - 1] = { transform: `translate${axis}(${to}%)` };
      this.dialog.setAttribute('data-spring-motion', '');
      this.sheetAnimation = this.dialog.animate(frames, { duration, easing: 'linear', fill: 'both' });
      // Cancellation during reopening is expected; consume the rejected finished promise.
      this.sheetAnimation.finished?.catch(() => {});
      return { animation: this.sheetAnimation, duration };
    }

    hide(returnFocus = true) {
      if (!returnFocus) { this.finishHide(false); return; }
      if (this.closing) return;
      if (!this.dialog?.open) { this.release(); return; }
      const start = this.sheetPosition(this.sheetSpring().axis);
      this.stopSheetMotion();
      this.closing = true;
      this.dialog.setAttribute('data-closing', '');
      const token = this.motionToken;
      const motion = this.animateSheet(true, start);
      if (!motion) { this.finishHide(); return; }
      this.video?.pause();
      const finish = () => { if (this.closing && token === this.motionToken) this.finishHide(); };
      motion.animation.finished?.then(finish, finish);
      this.closeTimer = setTimeout(finish, motion.duration + 120);
    }

    finishHide(returnFocus = true) {
      const wasOpen = this.dialog?.open;
      this.stopSheetMotion();
      if (wasOpen) this.dialog.close();
      this.release();
      if (wasOpen && returnFocus && this.trigger?.isConnected && !this.hidden) this.trigger.focus({ preventScroll: true });
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }
  });
}
